/** Document creation follows its project's signed ownership when sharing is on. */
import type { DocHandle, Repo } from '@automerge/automerge-repo/slim'

type Creator = <T>(projectId: string) => Promise<DocHandle<T>>
const creators = new WeakMap<Repo, Creator>()

/** Installed by the protected repo, never by a value supplied on the log. */
export function registerProjectDocumentCreator(repo: Repo, create: Creator) {
  creators.set(repo, create)
}
export async function createProjectOwnedDocument<T>(
  repo: Repo,
  projectId: string,
): Promise<DocHandle<T>> {
  const create = creators.get(repo)
  return create ? create<T>(projectId) : repo.create<T>()
}

type PersonalCreator = <T>(initialValue?: T) => Promise<DocHandle<T>>
const personal = new WeakMap<Repo, PersonalCreator>()

/** Installed when a sharing account opens: chrome that is nobody's but the
 * person's (spaces, layouts, presence) is owned by their person group. */
export function registerPersonalDocumentCreator(
  repo: Repo,
  create: PersonalCreator,
) {
  personal.set(repo, create)
}
export async function createPersonOwnedDocument<T>(
  repo: Repo,
  initialValue?: T,
): Promise<DocHandle<T>> {
  const create = personal.get(repo)
  return create ? create<T>(initialValue) : repo.create<T>(initialValue)
}

type CommentsCreator = (
  projectId: string,
  subjectId: string,
) => Promise<DocHandle<unknown>>
const commentsCreators = new WeakMap<Repo, CommentsCreator>()

/** Installed by the protected repo: the owner makes a page's comments
 * document with the page (RFC 0345 3g), since a viewer cannot link one. */
export function registerProjectCommentsCreator(
  repo: Repo,
  create: CommentsCreator,
) {
  commentsCreators.set(repo, create)
}
/** The new subject's comments document, or nothing on an open repo, where
 * the first comment still makes it. */
export async function createProjectComments(
  repo: Repo,
  projectId: string,
  subjectId: string,
): Promise<DocHandle<unknown> | undefined> {
  return commentsCreators.get(repo)?.(projectId, subjectId)
}
